import { useEffect, useRef, useState, type ReactNode } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/overlay";
import { formatTokens, totalTokens, type ProviderUsage } from "@/lib/ai-usage";
import { cn } from "@/lib/utils";

/**
 * Peças do monitor de uso das IAs (barra de status, canto inferior direito): o item que abre um
 * cartão ao passar o mouse (o clique fixa), com as cotas da conta e o uso por modelo, recurso e
 * projeto — como as extensões do Copilot e do Claude no VS Code.
 */
export const usageTrigger =
  "flex h-full items-center gap-1.5 whitespace-nowrap rounded-md px-2 outline-none transition-colors hover:bg-bg-muted hover:text-fg focus-visible:ring-2 focus-visible:ring-ring/60 data-[state=open]:bg-bg-muted data-[state=open]:text-fg";

export type UsageTone = "ok" | "low" | "out";

/** Pelo quanto já foi usado: a partir de 85% fica âmbar; esgotado, vermelho. */
export const toneOfUsed = (percentUsed: number | null): UsageTone =>
  percentUsed === null ? "ok" : percentUsed >= 100 ? "out" : percentUsed >= 85 ? "low" : "ok";

export const toneText: Record<UsageTone, string> = {
  ok: "",
  low: "text-[var(--warning)]",
  out: "text-[var(--danger)]",
};
const toneFill: Record<UsageTone, string> = {
  ok: "bg-fg",
  low: "bg-[var(--warning)]",
  out: "bg-[var(--danger)]",
};

/**
 * Item da barra de status com o cartão: abre ao passar o mouse e fecha ao sair (com folga para
 * chegar ao cartão); o clique fixa aberto até clicar fora.
 */
export function UsageHover({
  label,
  trigger,
  className,
  children,
}: {
  label: string;
  trigger: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const pinned = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const later = (fn: () => void, ms: number) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(fn, ms);
  };
  const keep = () => {
    if (timer.current) clearTimeout(timer.current);
  };
  const leave = () => {
    if (!pinned.current) later(() => setOpen(false), 280);
  };
  useEffect(() => keep, []);
  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) pinned.current = false;
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className={cn(usageTrigger, className)}
          onPointerEnter={(e) => {
            if (e.pointerType === "mouse") later(() => setOpen(true), 160);
          }}
          onPointerLeave={leave}
          onClick={(e) => {
            // Aberto pelo mouse: o clique só fixa (não fecha).
            if (open && !pinned.current) {
              e.preventDefault();
              keep();
              pinned.current = true;
              return;
            }
            pinned.current = !open;
          }}
        >
          {trigger}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="end"
        className="p-0"
        onPointerEnter={keep}
        onPointerLeave={leave}
        onOpenAutoFocus={(e) => {
          if (!pinned.current) e.preventDefault();
        }}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}

/** Número grande de uma cota ("100% usado"), com a renovação à direita e a barra embaixo. */
export function UsageStat({
  label,
  percentUsed,
  value,
  aside,
  hint,
}: {
  label: ReactNode;
  /** null: sem barra (ilimitado ou sem limite conhecido). */
  percentUsed: number | null;
  /** Texto grande; padrão: "N% usado". */
  value?: ReactNode;
  aside?: ReactNode;
  hint?: ReactNode;
}) {
  const used = percentUsed === null ? null : Math.max(0, percentUsed);
  const tone = toneOfUsed(used);
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[12.5px] font-semibold text-fg">{label}</span>
        {aside ? <span className="shrink-0 text-[11.5px] text-fg-subtle">{aside}</span> : null}
      </div>
      <div className={cn("flex items-baseline gap-1.5", toneText[tone])}>
        {value ?? (
          <>
            <span className="text-[22px] font-semibold leading-none tracking-tight tabular-nums">
              {Math.floor(used ?? 0)}%
            </span>
            <span className="text-[12px] text-fg-muted">usado</span>
          </>
        )}
      </div>
      {used !== null ? (
        <div
          className="h-1.5 overflow-hidden rounded-full bg-bg-emphasis"
          role="meter"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(Math.min(100, used))}
          aria-label={typeof label === "string" ? `${label}: usado` : undefined}
        >
          <div
            className={cn("h-full rounded-full transition-[width] duration-700 ease-out", toneFill[tone])}
            style={{ width: `${Math.max(2, Math.min(100, used))}%` }}
          />
        </div>
      ) : null}
      {hint ? <p className="text-[11.5px] leading-snug text-fg-subtle">{hint}</p> : null}
    </div>
  );
}

/** Linha "nome ……… valor" (como "Sugestões embutidas · Habilitado"). */
export function UsageLine({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="flex min-h-7 items-center justify-between gap-3 text-[12.5px]">
      <span className="text-fg-muted">{label}</span>
      <span className="flex items-center gap-2 text-fg">{children}</span>
    </div>
  );
}

export function UsageSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2.5">
      <h4 className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-fg-subtle">{title}</h4>
      {children}
    </section>
  );
}

/** Aviso dentro do cartão (orçamento adicional ligado, cota esgotada, conta não conectada). */
export function UsageNotice({ tone = "info", children }: { tone?: "info" | "warn"; children: ReactNode }) {
  return (
    <div
      className={cn(
        "rounded-lg border px-3 py-2.5 text-[12px] leading-snug",
        tone === "warn"
          ? "border-[var(--warning)]/40 bg-[var(--warning)]/10 text-fg"
          : "border-border bg-bg-subtle text-fg-muted",
      )}
    >
      {children}
    </div>
  );
}

/** Cartão do monitor: cabeçalho com a marca, o plano e uma ação; as cotas; ações embaixo. */
export function UsageCard({
  icon,
  title,
  subtitle,
  action,
  children,
  footer,
}: {
  icon: ReactNode;
  title: string;
  subtitle?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="w-[360px] max-w-[calc(100vw-24px)]">
      <div className="flex items-center gap-3 px-4 pb-3 pt-3.5">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-border bg-bg-emphasis text-fg">
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-semibold text-fg">{title}</div>
          {subtitle ? <div className="truncate text-[11.5px] text-fg-subtle">{subtitle}</div> : null}
        </div>
        {action}
      </div>
      <div className="max-h-[min(64vh,560px)] space-y-4 overflow-y-auto border-t border-border px-4 py-3.5">
        {children}
      </div>
      {footer ? (
        <div className="flex flex-wrap items-center gap-1 border-t border-border px-2 py-1.5">{footer}</div>
      ) : null}
    </div>
  );
}

export function UsageAction({
  icon,
  children,
  onClick,
  variant = "ghost",
}: {
  icon?: ReactNode;
  children: ReactNode;
  onClick: () => void;
  variant?: "ghost" | "outline" | "primary";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex h-7 shrink-0 items-center gap-1.5 rounded-md px-2 text-[12px] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring/60",
        variant === "ghost" && "text-fg-muted hover:bg-bg-muted hover:text-fg",
        variant === "outline" && "border border-border-strong px-2.5 font-medium text-fg hover:bg-bg-muted",
        variant === "primary" && "bg-fg px-3 font-medium text-fg-inverse hover:bg-fg/90",
      )}
    >
      {icon}
      {children}
    </button>
  );
}

/** Linhas com barra relativa ao maior (por projeto, modelo ou recurso). */
export function UsageRows({
  rows,
  current,
  describe,
  weight = (u) => u.cost * 1e6 || totalTokens(u) || u.requests,
  empty = "Nenhum uso neste mês ainda.",
  max: limit = 6,
}: {
  rows: Array<{ key: string; name: string; usage: ProviderUsage }>;
  current?: string | null;
  describe: (u: ProviderUsage) => string;
  weight?: (u: ProviderUsage) => number;
  empty?: string;
  max?: number;
}) {
  if (!rows.length) return <p className="text-[12px] text-fg-subtle">{empty}</p>;
  const shown = rows.slice(0, limit);
  const max = Math.max(1e-9, ...shown.map((r) => weight(r.usage)));
  return (
    <ul className="space-y-2.5">
      {shown.map((r) => {
        const mine = r.key === current;
        return (
          <li key={r.key} className="space-y-1">
            <div className="flex items-baseline justify-between gap-3 text-[12px]">
              <span className={cn("min-w-0 truncate", mine ? "font-semibold text-fg" : "text-fg-muted")}>
                {r.name}
                {mine ? (
                  <span className="ml-1.5 text-[10.5px] font-normal text-fg-subtle">este projeto</span>
                ) : null}
              </span>
              <span className="shrink-0 tabular-nums text-fg-subtle">{describe(r.usage)}</span>
            </div>
            <div className="h-1 overflow-hidden rounded-full bg-bg-emphasis">
              <div
                className={cn("h-full rounded-full", mine ? "bg-fg" : "bg-fg-subtle")}
                style={{ width: `${Math.max(3, (weight(r.usage) / max) * 100)}%` }}
              />
            </div>
          </li>
        );
      })}
      {rows.length > shown.length ? (
        <li className="text-[11.5px] text-fg-subtle">e mais {rows.length - shown.length}</li>
      ) : null}
    </ul>
  );
}

export const tokensLabel = (n: number) => `${formatTokens(n)} tokens`;

/** "2026-11-01" → "1 de novembro". */
export function formatDay(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString("pt-BR", { day: "numeric", month: "long" });
}

/** Instante ISO → "31 de out. às 21:00" (no fuso de quem vê). */
export function formatMoment(iso: string): string {
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return iso;
  const day = t.toLocaleDateString("pt-BR", { day: "numeric", month: "short" });
  const time = t.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  return `${day} às ${time}`;
}

/** Horário (do ISO) em que um limite por minuto enche de novo. */
export function formatClockTime(iso: string | undefined): string | null {
  if (!iso) return null;
  const t = new Date(iso);
  return Number.isNaN(t.getTime()) ? null : t.toLocaleTimeString("pt-BR");
}
